import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classnamesBind from 'classnames/bind';
import { withFormatter } from '@concur/nui-intl-runtime';
import AppHeaderContext from '../AppHeader/_AppHeaderContext';
import UserAvatar from '../AppHeader/_UserAvatar';

const getProfileName = ({
    actingAs,
    formatter,
    employeeDisplayName,
}) => {
    const { allUsers, companyName, otherUserName } = actingAs;

    if (companyName) {
        return companyName;
    }
    if (allUsers) {
        return formatter.formattedMessage({ id: 'CoreUI.allMyUsers' });
    }
    if (otherUserName) {
        return otherUserName;
    }
    return employeeDisplayName;
};

export const getProfileDisplayText = ({
    actingAs,
    formatter,
    employeeDisplayName,
    shortNames = false,
}) => {
    const { companyName } = actingAs;

    const name = getProfileName({ actingAs, formatter, employeeDisplayName });

    if (companyName) {
        return formatter.formattedMessage({ id: 'CoreUI.administeringCompany', values: { companyName } });
    }

    return shortNames
        ? formatter.formattedMessage({ id: 'CoreUI.actingAs', values: { userName: name } })
        : formatter.formattedMessage({ id: 'CoreUI.currentlyActingAs', values: { userName: name } });
};

const ProfileIdentifier = ({
    actingAs,
    cssBlock,
    formatter,
    lang,
    styles,
    isOpusUser,
    isCompleteTheme,
}) => {
    const classnames = classnamesBind.bind(styles);

    const { allUsers, companyName, otherUserName } = actingAs;
    const isActingForOthers = !!(allUsers || companyName || otherUserName);
    const { employeeDisplayName } = useContext(AppHeaderContext) || {};

    const descriptionClassName = classnames(`${cssBlock}__description`);

    return (
        <div className={classnames(`${cssBlock}__identifier`)} data-test="profile-identifier">
            <UserAvatar
                className={classnames(`${cssBlock}__avatar`)}
                formatter={formatter}
                lang={lang}
                employeeDisplayName={employeeDisplayName}
                size="sm"
                isOpusUser={isOpusUser}
                isCompleteTheme={isCompleteTheme}
            />
            <div className={classnames(`${cssBlock}__name`)} data-test="identifier">
                {employeeDisplayName}
                {isActingForOthers
                    && (
                        <div className={descriptionClassName} data-test="description">
                            {getProfileDisplayText({
                                actingAs, formatter, employeeDisplayName,
                            })}
                        </div>
                    )}
            </div>
        </div>
    );
};

ProfileIdentifier.displayName = 'ProfileIdentifier';

ProfileIdentifier.propTypes = {
    actingAs: PropTypes.shape({
        allUsers: PropTypes.bool,
        companyName: PropTypes.string,
        otherUserName: PropTypes.string,
    }),
    cssBlock: PropTypes.string,
    styles: PropTypes.object,
    lang: PropTypes.string,
    isOpusUser: PropTypes.bool,
    isCompleteTheme: PropTypes.bool,
};

export default withFormatter(ProfileIdentifier);
